import { type BpmnMeta, readBpmnMeta } from "./read-bpmn-meta";
import { defaultReadTextDeps, type ReadTextDeps } from "./read-file-text";

/** 利用者が選んだ（またはドロップした）BPMN ファイルの読み取り結果。 */
export type SelectedBpmnFile = {
  readonly fileName: string;
  readonly xml: string;
  readonly meta:
    | BpmnMeta
    /** 拡張子が .bpmn ではない。 */
    | { readonly kind: "not-bpmn" }
    /** ファイルを読めなかった（権限・破損など）。 */
    | { readonly kind: "unreadable" };
};

/**
 * 選んだファイルを読み、使えるかどうかをその場で判定する。
 *
 * 拡張子も見る。`input[type=file]` の `accept` はファイル選択の画面でしか効かず、
 * ドロップされたファイルは素通りするため。
 */
export async function readBpmnFile(
  file: File,
  deps: ReadTextDeps = defaultReadTextDeps,
): Promise<SelectedBpmnFile> {
  if (!file.name.toLowerCase().endsWith(".bpmn")) {
    return { fileName: file.name, xml: "", meta: { kind: "not-bpmn" } };
  }

  let xml: string;
  try {
    xml = await deps.readText(file);
  } catch {
    return { fileName: file.name, xml: "", meta: { kind: "unreadable" } };
  }

  return { fileName: file.name, xml, meta: readBpmnMeta(xml) };
}

/** 使えることが分かったファイル（実行プロセスを 1 つ読めた）。 */
export type ReadyBpmnFile = SelectedBpmnFile & {
  readonly meta: Extract<BpmnMeta, { readonly kind: "ok" }>;
};

export function isReadyBpmnFile(
  file: SelectedBpmnFile | null,
): file is ReadyBpmnFile {
  return file !== null && file.meta.kind === "ok";
}
